import React, { useEffect, useState, useRef } from 'react';
import Box from '@mui/material/Box';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { fetchGPSData } from '../../dashboard/store/dashboard.action';
import { selectgpsData, selectgpsDatasStatus } from '../../dashboard/store/dashboard.selectors';
import { loadGoogleMaps } from '../../../utils/common/googleMapsLoader';

const DriveMapComponent = (props) => {
  const { driveId, setSnackbar, videoRef } = props;

  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);
  const dispatch = useDispatch();
  const gpsData = useSelector(selectgpsData);
  const gpsDataLoading = useSelector(selectgpsDatasStatus);

  // Suppress ResizeObserver errors from map libraries
  useEffect(() => {
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
      }
    };

    const originalError = console.error;
    console.error = (...args) => {
      const message = args?.[0]?.toString?.() || '';
      if (message.includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    loadGoogleMaps('AIzaSyBrYTn6FUEGr_84F1UPtsYa7l_sp4bWWMo')
      .then(() => setMapLoaded(true))
      .catch(() => {
        setSnackbar({
          open: true,
          message: 'Failed to load map',
          severity: 'error',
        });
      });
  }, [setSnackbar]);

  // Fetch GPS data
  useEffect(() => {
    if (!driveId) return;
    dispatch(fetchGPSData(driveId));
  }, [driveId, dispatch]);

  // Create pin helper (icon-based pins, e.g. start/end)
  const createPin = (iconClass, bgColor, borderColor) => {
    const iconDiv = document.createElement('div');
    iconDiv.innerHTML = `<i class="${iconClass}"></i>`;
    iconDiv.style.fontSize = '22px';

    return new window.google.maps.marker.PinElement({
      glyph: iconDiv,
      glyphColor: '#FFF',
      background: bgColor,
      borderColor: borderColor,
      scale: 1.3,
    });
  };

  const createCircleMarkerContent = () => {
    const dot = document.createElement('div');
    dot.style.width = '16px';
    dot.style.height = '16px';
    dot.style.borderRadius = '50%';
    dot.style.backgroundColor = '#1976D2';
    dot.style.border = '2px solid #FFFFFF';
    dot.style.boxShadow = '0 0 2px rgba(0,0,0,0.5)';
    return dot;
  };

  // Initialize map
  useEffect(() => {
    if (!mapLoaded) return;

    const initMap = () => {
      const mapDiv = document.getElementById('drive-map');
      if (!mapDiv) {
        return;
      }

      mapRef.current = new window.google.maps.Map(mapDiv, {
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 4,
        mapId: '99b311845891871b',
      });
    };

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(initMap, 100);

    return () => {
      clearTimeout(timer);
      try {
        if (mapRef.current && mapRef.current.__drivePathElements) {
          const { borderPolyline, polyline, startMarker, endMarker, movingMarker } =
            mapRef.current.__drivePathElements;
          [borderPolyline, polyline, startMarker, endMarker, movingMarker].forEach((el) => {
            if (el) {
              if (typeof el.setMap === 'function') {
                el.setMap(null);
              } else {
                el.map = null;
              }
            }
          });
          mapRef.current.__drivePathElements = null;
        }
      } catch (e) {
        // Ignore errors during cleanup
      }
    };
  }, [mapLoaded]);

  // Render path with start/end markers
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || gpsDataLoading) return;

    // Remove previous path/markers
    if (mapRef.current.__drivePathElements) {
      const { borderPolyline, polyline, startMarker, endMarker } =
        mapRef.current.__drivePathElements;
      [borderPolyline, polyline, startMarker, endMarker].forEach((el) => {
        if (el) {
          if (typeof el.setMap === 'function') {
            el.setMap(null);
          } else {
            el.map = null;
          }
        }
      });
      mapRef.current.__drivePathElements = null;
    }

    if (!gpsData || gpsData.length === 0) {
      // Only show warning if loading has finished and data is still empty
      if (!gpsDataLoading) {
        console.warn('DriveMapComponent: No GPS data available');
        setSnackbar({
          open: true,
          message: 'GPS coordinates unavailable for this drive.',
          severity: 'warning',
        });
      }
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();

    const path = gpsData.map((point) => {
      const latLng = new window.google.maps.LatLng(point.latitude, point.longitude);
      bounds.extend(latLng);
      return {
        lat: point.latitude,
        lng: point.longitude,
      };
    });

    const center = {
      lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
      lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
    };

    mapRef.current.setCenter(center);

    const PADDING = 12;
    mapRef.current.fitBounds(bounds, PADDING);

    // Draw path with border
    const borderPolyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#004DA8',
      strokeOpacity: 1.0,
      strokeWeight: 8,
      zIndex: 1,
    });

    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: '#0075FF',
      strokeOpacity: 0.9,
      strokeWeight: 3,
      zIndex: 2,
    });

    borderPolyline.setMap(mapRef.current);
    polyline.setMap(mapRef.current);

    // Create start marker (green play icon)
    const startPin = createPin('fa fa-circle-play', '#24BD05', '#24BD05');
    const startMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: path[0],
      map: mapRef.current,
      title: 'Start',
      content: startPin.element,
    });

    // Create end marker (red stop icon)
    const endPin = createPin('fa fa-circle-stop', '#DC1709', '#DC1709');
    const endMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: path[path.length - 1],
      map: mapRef.current,
      title: 'End',
      content: endPin.element,
    });

    // Moving/video-position marker — same blue circular dot style as the
    // lane GPS map's live position marker.
    const movingMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: path[0],
      map: null,
      title: 'Video position',
      content: createCircleMarkerContent(),
      zIndex: 3,
    });

    const updateMovingMarker = () => {
      const video = videoRef?.current;
      if (!video || path.length === 0) {
        return;
      }

      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (duration <= 0 || path.length < 2) {
        movingMarker.map = null;
        return;
      }

      const progress = duration > 0 ? Math.min(video.currentTime / duration, 1) : 0;
      if (progress <= 0 || progress >= 1) {
        movingMarker.map = null;
        return;
      }

      movingMarker.map = mapRef.current;
      const segmentPosition = progress * (path.length - 1);
      const segmentIndex = Math.min(Math.floor(segmentPosition), path.length - 2);
      const segmentProgress = segmentPosition - segmentIndex;
      const currentPoint = path[segmentIndex] || path[0];
      const nextPoint = path[segmentIndex + 1] || currentPoint;

      movingMarker.position = {
        lat: currentPoint.lat + (nextPoint.lat - currentPoint.lat) * segmentProgress,
        lng: currentPoint.lng + (nextPoint.lng - currentPoint.lng) * segmentProgress,
      };
    };

    let animationFrame;
    const updateWhilePlaying = () => {
      updateMovingMarker();
      if (!videoRef?.current?.paused && !videoRef?.current?.ended) {
        animationFrame = window.requestAnimationFrame(updateWhilePlaying);
      }
    };
    const video = videoRef?.current;
    video?.addEventListener('timeupdate', updateMovingMarker);
    video?.addEventListener('loadedmetadata', updateMovingMarker);
    video?.addEventListener('seeking', updateMovingMarker);
    video?.addEventListener('play', updateWhilePlaying);
    video?.addEventListener('pause', updateMovingMarker);
    video?.addEventListener('ended', updateMovingMarker);
    updateMovingMarker();

    // Store references for cleanup
    mapRef.current.__drivePathElements = {
      borderPolyline,
      polyline,
      startMarker,
      endMarker,
      movingMarker,
    };

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
      video?.removeEventListener('timeupdate', updateMovingMarker);
      video?.removeEventListener('loadedmetadata', updateMovingMarker);
      video?.removeEventListener('seeking', updateMovingMarker);
      video?.removeEventListener('play', updateWhilePlaying);
      video?.removeEventListener('pause', updateMovingMarker);
      video?.removeEventListener('ended', updateMovingMarker);
      if (mapRef.current && mapRef.current.__drivePathElements) {
        const { borderPolyline, polyline, startMarker, endMarker, movingMarker } =
          mapRef.current.__drivePathElements;
        [borderPolyline, polyline, startMarker, endMarker, movingMarker].forEach((el) => {
          if (el) {
            if (typeof el.setMap === 'function') {
              el.setMap(null);
            } else {
              el.map = null;
            }
          }
        });
        mapRef.current.__drivePathElements = null;
      }
    };
  }, [gpsData, gpsDataLoading, mapLoaded, setSnackbar, videoRef]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        py: 2,
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        id="drive-map"
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      />
      {(gpsDataLoading || !mapLoaded) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 100,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

DriveMapComponent.propTypes = {
  driveId: PropTypes.string.isRequired,
  setSnackbar: PropTypes.func,
  videoRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

DriveMapComponent.defaultProps = {
  setSnackbar: () => {},
};

export default DriveMapComponent;
